/**
 * `<ProDocUploader>` — the **Upload** capability's package surface (ADR-0010):
 * drop zone, queue, per-file states, the duplicate choice.
 *
 * A file enters the suite here: posted to `prodoc-service`, which decides its
 * type by signature, checks its size, the storage limit and the duplicate, and
 * files it in a workspace under an `owner_app`, a `source_ref` and a declared
 * **document type** (FR-5 — the uploader says it; v1 never infers one). The
 * host says whose record it is (its own slug and key, or `prodoc` and none).
 *
 * A file ends one of five ways (plan §7, W3): **stored** — saying so when its
 * bytes are another type than its name claims ("stored as PNG"), and when it is
 * **encrypted**; **already here**, which asks *keep both*, *link as a new
 * version* or *discard* before anything is sent (D7); **refused** with the
 * service's reason (a damaged file names what is wrong with it); **discarded**;
 * or failed in transit, with *Try again*.
 *
 * W5 files a batch **in a folder** of its workspace (FR-38), and takes a
 * dropped **folder** whole (FR-7): its structure is made once under the chosen
 * folder and each file is filed where it sat — up to 200 files a drop.
 *
 * W9 lets a sibling host **name the folder path** (FR-39, D45): `folderPath`
 * beside `folderId` — `"FY 2025-26"` under ProDesk's folder for a client, say.
 * The batch is sent with `folder_path`, the service makes any folder missing on
 * the way once, kept by the host's app, and the person is shown the place
 * rather than asked for one.
 *
 * Engine W15 lets a host **ask for each file to be read** (FR-E40, T15.15):
 * `parse={{ profile: 'form16', review: 'app' }}` sends the upload's `parse`
 * field, and the service makes each version's job with the version — so a
 * file whose reading is refused is **not stored**, and its row says why. A
 * stored file's reading is followed on its row: being read, read and accepted,
 * waiting for review, or not read and why. **Without the prop nothing is
 * parsed**: the default stays store-only (ADR-0013), and no parse object is
 * ever sent.
 *
 * W22 (FR-55, D53): **a stored file says what happened in five words** —
 * *Stored*, *Reading*, *Needs review*, *Accepted*, *Failed* — each from the
 * job and its review, never a guess. A reading that needs a password shows **a
 * password box with *Unlock* on the row**: the same parse asked again with the
 * password, used once; a wrong one leaves the row *Failed* with *Wrong
 * password* and the box empty. A failed reading offers **Upload again**: the new
 * file becomes the next version of the same document, read as asked.
 *
 * W30 (FR-67, ADR-0021, D103): **a protected PDF asks for its password on its
 * row.** Each picked PDF is opened by pdf.js in this tab; one that needs a
 * password waits with a box, *Check* and *Skip — store it locked*. *Check*
 * tries it here — ✓ *Opens*, and the file is sent with it; or *That password
 * doesn't open this file* — and the service checks it again before it keeps
 * it, sealed: used for whoever may see the document, never shown to anyone.
 * The row then says what the service did: *Password saved*, or not and why (a
 * wrong one still stores the file, *Locked*). The password is never shown back,
 * never in browser storage, never in the console.
 *
 * Nothing here knows about a session, a router or an app's config: the host
 * passes `apiUrl`, `fetcher` and `orgId`, and links through `documentHref`.
 */
import { Alert, Badge, Button, Card, Checkbox, Dialog, EmptyState, FileInput, Input, RadioGroup, Select, Skeleton, TONE_TEXT, cn, formatDateTime } from '@fiveox/ui'
import {
  CircleAlert,
  CircleCheck,
  CircleSlash,
  Clock,
  CloudUpload,
  Copy,
  LoaderCircle,
  LockKeyhole,
  RotateCcw,
  ShieldCheck,
  X,
  type LucideIcon,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { openPdfWithPdfjs, type PdfOpener } from './evidence-page'
import { READING_WORDS, jobEnded, passwordErrorText, profileForType, readingReasonText, type ParseJob, type ReadingState } from './extraction-model'
import {
  DEFAULT_MAX_FILES_PER_REQUEST,
  DOCUMENT,
  EXTRACTION,
  FOLDER,
  MAX_FOLDER_DEPTH,
  acceptFor,
  documentTypeLabel,
  folderPathError,
  folderPathNames,
  formatBytes,
  type DocumentTypeOption,
  type FolderPath,
  type OnDuplicate,
  type ProDocClient,
  type ProDocDocument,
} from './prodoc-client'
import { FileMark, mediaLabel, plural, when } from './bits'
import { FolderPicker } from './folder-dialogs'
import { useFolderList } from './folder-tree'
import { PAIR_REFUSAL_TITLE, pairRefusalText, useLoad, useProDocAccess, useProDocClient, useWorkspaces, type HostProps } from './hooks'
import {
  MAX_FOLDER_DROP,
  PASSWORD_REASONS,
  addDroppedFolder,
  addFiles,
  checkPassword,
  clearFinished,
  filesOfEntries,
  noteHostOrg,
  readingOfItem,
  removeItem,
  resolveDuplicate,
  retryItem,
  skipPassword,
  unlockItem,
  uploadAgain,
  useUploads,
  type ParseProgress,
  type Twin,
  type UploadItem,
  type UploadParseRequest,
} from './upload-queue'

export interface ProDocUploaderProps extends HostProps {
  /** The slug of the app whose records these documents belong to (`prodoc` here). */
  ownerApp: string
  /** The host's own key for the record the documents attach to. */
  sourceRef?: string | null
  /** File into this workspace by default; the picker still offers the others the member sees. */
  workspaceId?: string | null
  /** Hide the workspace picker — a host that files into one place. */
  fixedWorkspace?: boolean
  /** File into this folder of the workspace by default (W5); the picker still offers the others. */
  folderId?: string | null
  /**
   * W9 (FR-39, D45): the folder path **your app** names — `"Clients/ACME
   * Traders/FY 2025-26"` or `["Clients", "ACME Traders", "FY 2025-26"]` —
   * under `folderId` when you give one (a folder of your own), else under the
   * workspace's top level. Every file is sent with it as `folder_path`; ProDoc
   * makes any folder missing on the way once, kept by your app, and files the
   * document in the last. The person sees the place instead of choosing one. A
   * sibling's `ownerApp` only: ProDoc's own uploads are filed where a person
   * chooses, and the service refuses a path for them.
   */
  folderPath?: FolderPath | null
  /** Declare this type by default; the picker still offers the others. */
  documentType?: string | null
  /** Where an uploaded document opens. Without it, the row is not a link. */
  documentHref?: (document: ProDocDocument) => string
  /** Rendered inside the host's own link — e.g. react-router's `<Link>` — so the package imports no router. */
  renderLink?: (href: string, children: ReactNode, className?: string) => ReactNode
  /** Fired for each document the service stored. */
  onUploaded?: (document: ProDocDocument) => void
  /** Something to offer when the batch is empty — the host's "open the library" button. */
  emptyAction?: ReactNode
  /**
   * Where more storage is bought — the store's ProDoc listing. A refused upload
   * with `USAGE_LIMIT_EXCEEDED` is the storage limit of the plan, which is a
   * paywall, not a fault: the refusal says so and links here. This app never
   * calls the store; it only links to it (constitution §1).
   */
  storeUrl?: string
  /**
   * Engine W15 (FR-E40, T15.15): read each file as this profile — the
   * document type's code (`invoice`, `bank_statement`, `form16`) or `auto` —
   * as it is stored. A type another profile reads is sent as that profile
   * (`PROFILE_FOR_TYPE`, engine W17): `credit_note` and `debit_note` as `invoice`. `review` is `auto` (ProDoc's review, the default) or
   * `app` (your app reviews your own documents, T15.14); `context` names whose
   * books (`gstins`, `pan`) and the period (FR-E7). **Omit it and nothing is
   * parsed.** The person needs `extraction:can_create`. The reading is
   * committed with the upload: a refused one stores nothing, and the file's
   * row says why.
   */
  parse?: UploadParseRequest | null
  /** Fired once for each file whose reading ended — read, refused or failed — with its job. */
  onParsed?: (job: ParseJob, document: ProDocDocument) => void
  /** How soon a parse job is first asked about again. A test passes a few milliseconds. */
  jobPollMs?: number
  /**
   * W22 (FR-59, D57): files to add as soon as the organisation's upload
   * settings are read — dropped onto a screen before the popup was open. Each
   * array is added once, however often the uploader is drawn.
   */
  initialFiles?: File[]
  /** W22: a dropped folder's entries (`entriesOf(dataTransfer)`, taken in the drop event), added as a folder drop is (FR-7). */
  initialEntries?: FileSystemEntry[]
  /**
   * W30 (FR-67, D103): how a picked PDF is opened in this tab — to see whether
   * it needs a password, and to check one before it is sent. PDF.js unless a
   * host or a test says otherwise; `null` opens nothing, and no PDF asks.
   */
  openPdf?: PdfOpener | null
  className?: string
}

/** The arrays of files or entries already added — once each, across the popup's openings. */
const added = new WeakSet<object>()

export function ProDocUploader(props: ProDocUploaderProps) {
  const { ownerApp, sourceRef = null, fixedWorkspace = false, documentHref, renderLink, onUploaded, onParsed, emptyAction, storeUrl, className } = props
  const client = useProDocClient(props)
  const access = useProDocAccess(client)
  const options = useLoad(() => client.documents.options(), [client])
  const workspaces = useWorkspaces(client)
  const items = useUploads(props.orgId)
  // FR-E40: asked for by the host, and the person's to ask (FR-E39) — otherwise not sent at all.
  // A type's code is sent as the profile that reads it (D54, W17): `credit_note` is read as `invoice`.
  const parseAsked = props.parse && props.parse.profile.trim() ? { ...props.parse, profile: profileForType(props.parse.profile.trim()) } : null
  const mayParse = Boolean(parseAsked) && access.can(EXTRACTION, 'can_create')

  const parsedAnnounced = useRef(new Set<string>())
  useEffect(() => {
    if (!onParsed) return
    for (const item of items) {
      const job = item.parse?.job
      if (job && item.document && jobEnded(job) && !parsedAnnounced.current.has(item.key)) {
        parsedAnnounced.current.add(item.key)
        onParsed(job, item.document)
      }
    }
  }, [items, onParsed])

  const [into, setInto] = useState<string | null>(props.workspaceId ?? null)
  useEffect(() => setInto(props.workspaceId ?? null), [props.workspaceId])
  const [folderInto, setFolderInto] = useState<string | null>(props.folderId ?? null)
  useEffect(() => setFolderInto(props.folderId ?? null), [props.folderId])
  const [dropping, setDropping] = useState(false)
  const [dropError, setDropError] = useState('')
  const [type, setType] = useState<string>(props.documentType ?? '')
  useEffect(() => setType(props.documentType ?? ''), [props.documentType])

  const announced = useRef(new Set<string>())
  useEffect(() => {
    if (!onUploaded) return
    for (const item of items) {
      if (item.document && item.state === 'done' && !announced.current.has(item.key)) {
        announced.current.add(item.key)
        onUploaded(item.document)
      }
    }
  }, [items, onUploaded])

  // The duplicate dialog opens itself for the first file waiting for an
  // answer; closing it leaves the file waiting, with *Choose…* on its row.
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const [chosen, setChosen] = useState<string | null>(null)
  const asking = items.filter((i) => i.state === 'asking')
  const deciding = asking.find((i) => i.key === chosen) ?? asking.find((i) => !dismissed.has(i.key)) ?? null

  const o = options.data
  const accept = useMemo(() => (o ? acceptFor(o.media_types) : ''), [o])
  const types = o?.document_types ?? []
  // The host's named place (FR-39): its names as the service will walk them, and what is wrong with them, if anything.
  const named = folderPathNames(props.folderPath)
  const namedError = folderPathError(props.folderPath)

  const batchOptions = () =>
    o && {
      mediaTypes: o.media_types,
      maxBytes: o.max_upload_bytes,
      maxFiles: o.max_files_per_request ?? DEFAULT_MAX_FILES_PER_REQUEST,
      workspaceId: into,
      folderId: folderInto,
      folderPath: named.length ? named : null,
      documentType: type || null,
      ownerApp,
      sourceRef,
      owner: props.orgId,
      parse: mayParse ? parseAsked : null,
      jobPollMs: props.jobPollMs,
      openPdf: props.openPdf === undefined ? openPdfWithPdfjs : props.openPdf,
    }
  const add = (files: FileList | File[] | null) => {
    const opts = batchOptions()
    if (!files || !opts) return
    setDropError('')
    addFiles(client, files, opts)
  }
  /** A dropped folder (FR-7): its structure made once, each file filed where it sat. */
  const addFolder = async (entries: FileSystemEntry[]) => {
    const opts = batchOptions()
    if (!opts) return
    setDropError('')
    if (!access.can(FOLDER, 'can_create')) {
      setDropError('Dropping a folder makes folders, and your ProDoc role does not include making them. Drop the files themselves, or ask an administrator.')
      return
    }
    const workspaceId = into ?? workspaces.list.find((w) => w.is_default)?.id ?? null
    if (!workspaceId) {
      setDropError('Choose a workspace for the folder first — the workspaces have not loaded yet.')
      return
    }
    setDropping(true)
    try {
      const files = await filesOfEntries(entries)
      const result = await addDroppedFolder(client, files, { ...opts, workspaceId })
      if (!result.ok) setDropError(result.message)
    } catch {
      setDropError('The folder could not be read from the drop. Try dropping it again, or its files.')
    } finally {
      setDropping(false)
    }
  }
  const dragging = useWindowDrop(add, (entries) => void addFolder(entries))

  // W22 (FR-59): an org switch the person went ahead with stops the previous organisation's files not yet sent.
  useEffect(() => noteHostOrg(ownerApp, props.orgId), [ownerApp, props.orgId])

  // W22 (D57): what was dropped before the popup opened joins the batch once the settings are here.
  const mayUpload = !access.loading && access.can(DOCUMENT, 'can_create')
  useEffect(() => {
    if (!o || !mayUpload) return
    const files = props.initialFiles
    if (files?.length && !added.has(files)) {
      added.add(files)
      add(files)
    }
    const entries = props.initialEntries
    if (entries?.length && !added.has(entries)) {
      added.add(entries)
      if (entries.some((e) => e.isDirectory)) void addFolder(entries)
      else void Promise.all(entries.filter((e) => e.isFile).map((e) => new Promise<File>((resolve, reject) => (e as FileSystemFileEntry).file(resolve, reject)))).then(add, () => {})
    }
    // The batch's options are read when the files are added; the arrays decide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [o, mayUpload, props.initialFiles, props.initialEntries])

  if (access.loading) {
    return <Skeleton className={cn('h-72 rounded-2xl', className)} />
  }

  if (!access.can(DOCUMENT, 'can_create')) {
    return (
      <EmptyState
        title="You can’t upload here"
        description="Your ProDoc role in this organisation does not include uploading. An administrator can change it in the FiveOX console."
        className={className}
      />
    )
  }

  const inFlight = items.filter((i) => i.state === 'checking' || i.state === 'queued' || i.state === 'uploading').length
  const stored = items.filter((i) => i.state === 'done').length
  const notUploaded = items.filter((i) => i.state === 'refused' || i.state === 'error').length
  const discarded = items.filter((i) => i.state === 'discarded').length
  // Two plan limits can refuse a file: the storage limit (FR-6), and — for a file asked to be read — the documents-processed one (FR-E35, T15.15).
  const limitReached = items.some((i) => i.code === 'USAGE_LIMIT_EXCEEDED' && !i.parseRefused)
  const readingLimitReached = items.some((i) => i.code === 'USAGE_LIMIT_EXCEEDED' && i.parseRefused)
  // A sibling's pair refused the batch (FR-33): a connection to turn on, not a missing record and not the paywall.
  const pairRefused = items.find((i) => i.pair)?.pair ?? null
  const list = workspaces.list
  const fallback = list.find((w) => w.is_default) ?? null
  const target = list.find((w) => w.id === into) ?? fallback

  const resolve = (item: UploadItem, choice: OnDuplicate | 'discard', linkTo: string | undefined, rest: boolean) => {
    void resolveDuplicate(item.key, choice, linkTo)
    if (rest) {
      // *The same for the rest*: each other file links to its own earlier copy.
      for (const other of asking) if (other.key !== item.key) void resolveDuplicate(other.key, choice, other.twins[0]?.id)
    }
    setChosen(null)
  }

  return (
    <div className={cn('space-y-6', className)}>
      {limitReached && (
        // The storage limit is the plan's, not a fault (FR-6): the copy reads
        // like the paywall — paused, nothing lost, the store is the door.
        <Alert tone="warning">
          <p className="font-medium">This organisation’s storage limit is reached</p>
          <p className="mt-1">
            New files are not being stored until the plan has more room. Everything already stored is still here, exactly as it was.
            {storeUrl ? (
              <>
                {' '}
                <a href={storeUrl} className="font-medium underline underline-offset-2">
                  Add storage in the FiveOX Store →
                </a>
              </>
            ) : (
              ' An administrator can add storage in the FiveOX Store.'
            )}
          </p>
        </Alert>
      )}

      {readingLimitReached && (
        // The reading's limit is the plan's too (FR-E35): paused, nothing lost — and, the
        // reading being part of the upload here, the files that asked for it were not stored.
        <Alert tone="warning">
          <p className="font-medium">This organisation’s documents-processed limit is reached</p>
          <p className="mt-1">
            ProDoc is not reading new documents until the plan allows more, and a file asked to be read is stored only with its reading — so these were not
            stored. Everything already stored is still here.
            {storeUrl ? (
              <>
                {' '}
                <a href={storeUrl} className="font-medium underline underline-offset-2">
                  See the plans in the FiveOX Store →
                </a>
              </>
            ) : (
              ' An administrator can change the plan in the FiveOX Store.'
            )}
          </p>
        </Alert>
      )}

      {pairRefused && (
        <Alert tone="warning">
          <p className="font-medium">{PAIR_REFUSAL_TITLE}</p>
          <p className="mt-1">{pairRefusalText(pairRefused).detail} The files below were not stored.</p>
        </Alert>
      )}

      {namedError && (
        // The host's path, not the person's choice: said once here, and every file refused with it.
        <Alert tone="error">
          <p className="font-medium">This app names a folder ProDoc cannot make</p>
          <p className="mt-1">{namedError} Files added here are not stored until the app names a folder that can be made.</p>
        </Alert>
      )}

      {parseAsked && !mayParse && (
        // The host asked for reading; this person may not ask for it (FR-E39). Files are stored all the same.
        <Alert tone="info">
          <p>Files added here are stored but not read: your ProDoc role does not include asking ProDoc to read a document. An administrator can change it in the FiveOX console.</p>
        </Alert>
      )}

      {dropError && (
        <Alert tone="error">
          <p>{dropError}</p>
        </Alert>
      )}
      {dropping && (
        <Alert tone="info">
          <p>Reading the folder and making its folders…</p>
        </Alert>
      )}

      {items.length > 0 && (
        <BatchHeadline
          total={items.length}
          inFlight={inFlight}
          stored={stored}
          waiting={asking.length}
          notUploaded={notUploaded}
          discarded={discarded}
          onClear={() => clearFinished(props.orgId)}
        />
      )}

      <Card className={cn('overflow-hidden transition-shadow', dragging && 'ring-primary ring-2')}>
        {options.loading && !o ? (
          <Skeleton className="m-6 h-56 rounded-2xl" />
        ) : !o ? (
          <div className="p-6">
            <Alert tone="error">
              Could not read this organisation’s upload settings: {options.error || 'the service did not answer.'}{' '}
              <Button variant="link" onClick={() => void options.reload()}>
                Try again
              </Button>
            </Alert>
          </div>
        ) : (
          <div className="p-4 sm:p-6">
            <div
              className={cn(
                'rounded-2xl border-2 border-dashed px-6 text-center transition-colors',
                items.length ? 'py-6' : 'py-10',
                dragging ? 'border-primary bg-primary/5' : 'border-subtle bg-muted/30',
              )}
            >
              {items.length === 0 && (
                <div className="relative mx-auto mb-4 flex h-16 w-28 items-center justify-center" aria-hidden>
                  <span className="absolute top-3 left-0 -rotate-12">
                    <FileMark mediaType="text/csv" />
                  </span>
                  <span className="bg-primary text-primary-foreground relative z-10 flex h-14 w-14 items-center justify-center rounded-2xl shadow-lg">
                    <CloudUpload size={28} strokeWidth={1.75} />
                  </span>
                  <span className="absolute top-3 right-0 rotate-12">
                    <FileMark mediaType="image/png" />
                  </span>
                </div>
              )}
              <p className="text-foreground text-lg font-semibold">
                {items.length ? 'Drop more files or a folder anywhere on this page' : 'Drop files or a folder anywhere on this page'}
              </p>
              <p className="text-muted-foreground mt-1 text-sm">
                {describeTypes(o.media_types)} · up to {formatBytes(o.max_upload_bytes)} each · as many as you like · a folder of up to {MAX_FOLDER_DROP} keeps its
                structure
              </p>
              <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm">
                {!fixedWorkspace && (
                  <WorkspaceChoice
                    choices={list}
                    value={into}
                    loading={workspaces.loading}
                    error={workspaces.error}
                    onChange={(id) => {
                      setInto(id || null)
                      // A folder belongs to one workspace: another workspace starts at its top level.
                      setFolderInto(null)
                    }}
                  />
                )}
                {named.length > 0 ? (
                  <NamedFolder client={client} workspaceId={target?.id ?? into} baseId={folderInto} names={named} inSight={access.can(FOLDER, 'can_view')} />
                ) : (
                  access.can(FOLDER, 'can_view') &&
                  (target?.id ?? into) && (
                    <span className="flex items-center gap-2">
                      <span className="text-foreground font-medium">Folder</span>
                      <FolderPicker
                        client={client}
                        workspaceId={target?.id ?? into}
                        value={folderInto}
                        onChange={setFolderInto}
                        label="Folder"
                        rootLabel="Top level"
                        size="sm"
                        app={o.app}
                      />
                    </span>
                  )
                )}
                <TypeChoice types={types} value={type} onChange={setType} />
              </div>
              <p className="text-muted-foreground mt-2 text-xs">
                {target ? `Everyone who sees ${target.name} sees these files.` : 'Filed in the organisation’s default workspace, which every member sees.'} The type is
                what you say it is; ProDoc does not guess.
                {mayParse && parseAsked
                  ? ` Each file is stored and read as ${parseAsked.profile === 'auto' ? 'what ProDoc finds it is' : documentTypeLabel(parseAsked.profile, types).toLowerCase()} together — one that cannot be read that way is not stored.`
                  : ''}
              </p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                <Picker accept={accept} onAdd={add} label={items.length ? 'Add files' : 'Choose files'} />
                {items.length === 0 && emptyAction}
              </div>
            </div>
            {items.length === 0 && (
              <p className="text-muted-foreground mt-4 flex items-start justify-center gap-2 text-center text-sm">
                <ShieldCheck size={16} className="text-primary mt-0.5 shrink-0" aria-hidden />
                Each file is checked by its contents, hashed and stored in this organisation’s private store; a copy of a file already here is
                reported, and you choose what happens — never merged.
              </p>
            )}
          </div>
        )}
      </Card>

      {items.length > 0 && (
        <Card>
          <div className="flex min-h-8 flex-wrap items-center justify-between gap-2 px-5 pt-4">
            <h2 className="text-foreground font-semibold">
              This batch{' '}
              <span className="text-muted-foreground text-sm font-normal">
                · {items.length - inFlight - asking.length} of {items.length} done
              </span>
            </h2>
            {items.length > inFlight + asking.length && (
              <Button variant="ghost" size="sm" onClick={() => clearFinished(props.orgId)}>
                Clear finished
              </Button>
            )}
          </div>
          {inFlight > 0 && (
            <div
              role="progressbar"
              aria-label="Batch progress"
              aria-valuemin={0}
              aria-valuemax={items.length}
              aria-valuenow={items.length - inFlight}
              className="bg-muted mx-5 mt-3 h-1.5 overflow-hidden rounded-full"
            >
              <div
                className="bg-primary h-full rounded-full transition-all duration-300"
                style={{ width: `${((items.length - inFlight) / items.length) * 100}%` }}
              />
            </div>
          )}
          <ul className="divide-border mt-2 divide-y px-2 pb-2">
            {items.map((item) => (
              <BatchRow
                key={item.key}
                item={item}
                accept={accept}
                workspaceName={workspaces.nameOf(item.document?.workspace_id ?? item.workspaceId) ?? fallback?.name ?? null}
                typeLabel={documentTypeLabel(item.document?.document_type ?? item.documentType, types)}
                labelOf={(code) => documentTypeLabel(code, types)}
                documentHref={documentHref}
                renderLink={renderLink}
                onChoose={() => {
                  setChosen(item.key)
                  setDismissed((d) => new Set([...d].filter((k) => k !== item.key)))
                }}
              />
            ))}
          </ul>
        </Card>
      )}

      <DuplicateDialog
        item={deciding}
        others={Math.max(0, asking.length - 1)}
        client={client}
        nameOf={workspaces.nameOf}
        onClose={() => {
          if (deciding) setDismissed((d) => new Set(d).add(deciding.key))
          setChosen(null)
        }}
        onResolve={resolve}
      />
    </div>
  )
}

/* ------------------------------------------------------------- choices */

/** Which workspace the batch is filed in — the people who see it. The
 *  organisation's default workspace when nothing else is chosen (D9); there
 *  is no "no workspace" any more. */
function WorkspaceChoice({
  choices,
  value,
  loading,
  error,
  onChange,
}: {
  choices: { id: string; name: string; is_default?: boolean }[]
  value: string | null
  loading: boolean
  error: string
  onChange: (id: string) => void
}) {
  if (loading && choices.length === 0) return null
  if (error) {
    return <p className={cn('text-sm', TONE_TEXT.error)}>Could not load workspaces: {error}</p>
  }
  const fallback = choices.find((w) => w.is_default)
  const others = choices.filter((w) => !w.is_default)
  return (
    <span className="flex items-center gap-2">
      <label htmlFor="prodoc-upload-workspace" className="text-foreground font-medium">
        Workspace
      </label>
      <Select id="prodoc-upload-workspace" size="sm" value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">{fallback ? `${fallback.name} (default)` : 'The default workspace'}</option>
        {others.map((w) => (
          <option key={w.id} value={w.id}>
            {w.name}
          </option>
        ))}
      </Select>
    </span>
  )
}

/**
 * Where the host files the batch when it names the path (FR-39, D45): its
 * folder — named from the tree when the person may see folders — then the
 * path, said and not offered as a choice. Folders missing on the way are made
 * by the service when the first file arrives, kept by the host's app.
 */
function NamedFolder({
  client,
  workspaceId,
  baseId,
  names,
  inSight,
}: {
  client: ProDocClient
  workspaceId: string | null
  baseId: string | null
  names: string[]
  inSight: boolean
}) {
  const tree = useFolderList(client, inSight ? workspaceId : null)
  const byId = new Map((tree.data ?? []).map((f) => [f.id, f]))
  const base: string[] = []
  // Up the parents to the top level; the depth bounds the walk whatever the list says.
  for (let f = baseId ? byId.get(baseId) : undefined, n = 0; f && n <= MAX_FOLDER_DEPTH; f = f.parent_id ? byId.get(f.parent_id) : undefined, n += 1) {
    base.unshift(f.name)
  }
  const shown = [...(baseId && base.length === 0 ? ['…'] : base), ...names]
  return (
    <span className="flex items-center gap-2">
      <span className="text-foreground font-medium">Folder</span>
      <span className="text-muted-foreground">{shown.join(' › ')}</span>
    </span>
  )
}

/** The document type the batch is declared as — the organisation's enabled list (FR-5). */
function TypeChoice({ types, value, onChange }: { types: DocumentTypeOption[]; value: string; onChange: (code: string) => void }) {
  const enabled = types.filter((t) => t.enabled !== false && t.code !== 'other')
  return (
    <span className="flex items-center gap-2">
      <label htmlFor="prodoc-upload-type" className="text-foreground font-medium">
        Type
      </label>
      <Select id="prodoc-upload-type" size="sm" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{documentTypeLabel('other', types)}</option>
        {enabled.map((t) => (
          <option key={t.code} value={t.code}>
            {t.label}
          </option>
        ))}
      </Select>
    </span>
  )
}

/* -------------------------------------------------------------- headline */

function BatchHeadline({
  total,
  inFlight,
  stored,
  waiting,
  notUploaded,
  discarded,
  onClear,
}: {
  total: number
  inFlight: number
  stored: number
  waiting: number
  notUploaded: number
  discarded: number
  onClear: () => void
}) {
  const uploading = inFlight > 0
  const Icon = uploading ? LoaderCircle : waiting ? Copy : CircleCheck
  return (
    <Card className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center">
      <span
        className={cn(
          'bg-muted flex h-11 w-11 shrink-0 items-center justify-center rounded-full',
          uploading ? TONE_TEXT.info : waiting ? TONE_TEXT.warning : TONE_TEXT.success,
        )}
        aria-hidden
      >
        <Icon size={22} strokeWidth={1.75} className={uploading ? 'animate-spin' : undefined} />
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="text-foreground text-lg font-semibold">
          {uploading
            ? `Uploading ${inFlight} of ${plural(total, 'file')}…`
            : waiting
              ? `${plural(waiting, 'file')} already in ProDoc — choose what happens`
              : 'This batch is done'}
        </h2>
        <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-2 text-sm">
          {stored > 0 && <Badge tone="success">{stored} stored</Badge>}
          {waiting > 0 && <Badge tone="warning">{waiting} already here</Badge>}
          {notUploaded > 0 && <Badge tone="error">{notUploaded} not uploaded</Badge>}
          {discarded > 0 && <Badge tone="neutral">{discarded} discarded</Badge>}
          <span>{uploading ? 'You can leave this page — uploads carry on.' : 'Everything stored is in Documents.'}</span>
        </div>
      </div>
      {!uploading && !waiting && (
        <Button variant="ghost" onClick={onClear}>
          Clear the list
        </Button>
      )}
    </Card>
  )
}

/* ------------------------------------------------------------------ rows */

/** A refusal's label, by the service's reason — what the person can act on. */
function refusalLabel(item: UploadItem): string {
  if (item.code === 'USAGE_LIMIT_EXCEEDED') return item.parseRefused ? 'Reading limit reached' : 'Storage limit reached'
  if (item.pair) return 'Not connected'
  // T15.15: the reading it asked for was refused, so it was not stored.
  if (item.code === 'PROFILE_MEDIA_MISMATCH') return 'Cannot be read as asked'
  if (item.parseRefused) return 'Reading refused'
  switch (item.reason) {
    case 'corrupt':
      return 'Damaged file'
    case 'unsupported_type':
      return 'Not a type ProDoc stores'
    case 'too_large':
      return 'Too large'
    case 'too_many_files':
      return 'Too many at once'
    default:
      return 'Not uploaded'
  }
}

/** What a stored file's line says: where it went, its size, and anything the person should know. */
function storedLine(item: UploadItem, doc: ProDocDocument, workspaceName: string | null, typeLabel: string): string {
  const parts: string[] = []
  if (item.choice === 'new_version') parts.push(`added as version ${doc.version_count} of “${doc.title}”`)
  else parts.push(typeLabel)
  if (workspaceName) parts.push(workspaceName)
  parts.push(formatBytes(doc.size_bytes))
  // FR-2: the bytes decide the type, and the screen says so when the name disagreed.
  if (item.expectedType && doc.media_type !== item.expectedType) parts.push(`stored as ${mediaLabel(doc.media_type)} — its contents, not its name, decide`)
  if (item.choice === 'keep_both') parts.push('a second copy, as you chose')
  return parts.join(' · ')
}

/** A stored file's reading (FR-E40, T15.15), on its row: waiting, being read, read — and how its review stands — or not read, and why. */
function ParseLine({ parse, labelOf }: { parse: ParseProgress; labelOf: (code: string) => string }) {
  const profile = parse.job?.profile
  const as = profile && profile !== 'auto' ? labelOf(profile).toLowerCase() : 'what ProDoc finds it is'
  const going = parse.state === 'queued' || parse.state === 'running'
  const notRead = parse.state === 'refused' || parse.state === 'failed' || parse.state === 'error'
  const text =
    parse.state === 'queued'
      ? `Waiting to be read as ${as}…`
      : parse.state === 'running'
        ? `Being read as ${as}…`
        : parse.state === 'completed'
          ? `Read as ${as}${parse.review === 'accepted' ? ' — accepted' : parse.review === 'needs_review' ? ' — waiting for a reviewer' : parse.review === 'app_review' ? ' — for its app to review' : ''}.`
          : `Not read${parse.message ? `: ${parse.message}` : '.'} The file is stored.`
  const Icon = going ? LoaderCircle : notRead ? CircleAlert : parse.review === 'needs_review' ? Clock : CircleCheck
  return (
    <p className={cn('mt-1 flex items-center gap-1.5 text-xs', notRead ? TONE_TEXT.warning : parse.state === 'completed' ? TONE_TEXT.success : 'text-muted-foreground')} data-parse={parse.state}>
      <Icon size={13} className={cn('shrink-0', going && 'animate-spin')} aria-hidden />
      {text}
    </p>
  )
}

/** FR-55's five words on a stored row: an icon and a tone each — a failure the only red. */
const READING_LOOK: Record<ReadingState, [LucideIcon, string]> = {
  none: [CircleCheck, TONE_TEXT.success],
  reading: [LoaderCircle, 'text-primary'],
  needs_review: [Clock, TONE_TEXT.warning],
  accepted: [CircleCheck, TONE_TEXT.success],
  failed: [CircleAlert, TONE_TEXT.error],
}

function BatchRow({
  item,
  accept,
  workspaceName,
  typeLabel,
  labelOf,
  documentHref,
  renderLink,
  onChoose,
}: {
  item: UploadItem
  accept: string
  workspaceName: string | null
  typeLabel: string
  labelOf: (code: string) => string
  documentHref?: (document: ProDocDocument) => string
  renderLink?: (href: string, children: ReactNode, className?: string) => ReactNode
  onChoose: () => void
}) {
  const doc = item.state === 'done' ? (item.document ?? null) : null
  const refused = item.state === 'refused' || item.state === 'error'
  const limit = item.code === 'USAGE_LIMIT_EXCEEDED'
  // W22 (FR-55, D53): a stored file's word — its reading's, from the job and its review.
  const reading = readingOfItem(item)
  const again = item.again ?? null
  const look = reading ? READING_LOOK[reading.state] : null
  // W30 (FR-67): the file waits for its password — the box, a check under way — before it is sent.
  const lock = item.lock ?? null
  const waitsForPassword = !doc && !refused && item.state !== 'discarded' && (lock?.state === 'asking' || lock?.state === 'checking')

  const [Icon, tone, label]: [LucideIcon, string, string] = refused
    ? [CircleAlert, limit ? TONE_TEXT.warning : TONE_TEXT.error, item.state === 'error' ? 'Not uploaded' : refusalLabel(item)]
    : again?.state === 'uploading'
      ? [LoaderCircle, 'text-primary', 'Uploading']
      : doc && reading && look
        ? [look[0], look[1], READING_WORDS[reading.state]]
        : item.state === 'asking'
          ? [Copy, TONE_TEXT.warning, 'Already in ProDoc']
          : item.state === 'discarded'
            ? [CircleSlash, 'text-muted-foreground', 'Discarded']
            : item.state === 'uploading'
              ? [LoaderCircle, 'text-primary', 'Uploading']
              : waitsForPassword
                ? [LockKeyhole, TONE_TEXT.warning, 'Needs its password']
                : [Clock, 'text-muted-foreground', item.state === 'checking' ? 'Checking' : 'Waiting']

  const line = refused
    ? item.message
    : again?.state === 'uploading' && doc
      ? `Uploading ${again.fileName} as version ${doc.version_count + 1} of “${doc.title}”…`
      : doc
        ? storedLine(item, doc, workspaceName, typeLabel)
        : item.state === 'asking'
          ? 'The same file is already stored here. Nothing is sent until you choose.'
          : item.state === 'discarded'
            ? 'Not stored — the earlier document is unchanged.'
            : item.state === 'uploading'
              ? `Uploading — ${item.progress}%`
              : waitsForPassword
                ? `This PDF has a password · ${formatBytes(item.file.size)}`
                : item.state === 'checking'
                  ? `Looking for an earlier copy · ${formatBytes(item.file.size)}`
                  : `Waiting · ${formatBytes(item.file.size)}`

  const href = doc && documentHref ? documentHref(doc) : null
  const nameClass = 'text-foreground block truncate text-sm font-medium'
  // A file from a dropped folder says where it sat: `a/b/x.pdf`.
  const shown = item.relativePath ?? item.file.name
  const name = href ? (
    renderLink ? (
      renderLink(href, shown, `${nameClass} hover:underline`)
    ) : (
      <a href={href} className={`${nameClass} hover:underline`}>
        {shown}
      </a>
    )
  ) : (
    <p className={nameClass} title={shown}>
      {shown}
    </p>
  )

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-3">
      <FileMark mediaType={doc?.media_type ?? item.expectedType ?? item.file.type} size="sm" />
      <div className="min-w-0 flex-1 basis-56">
        {name}
        <p className={cn('line-clamp-2 text-xs', refused ? (limit ? TONE_TEXT.warning : TONE_TEXT.error) : 'text-muted-foreground')}>{line}</p>
        {doc?.encrypted && item.passwordSaved !== true && (
          <p className={cn('mt-1 text-xs', TONE_TEXT.warning)}>Encrypted — stored exactly as it is; it may need its password to open.</p>
        )}
        {doc && <PasswordOutcome item={item} />}
        {waitsForPassword && <PasswordAtUpload item={item} />}
        {!doc && !refused && lock?.state === 'opens' && (
          <p className={cn('mt-1 flex items-center gap-1 text-xs', TONE_TEXT.success)} data-lock="opens">
            <CircleCheck size={13} aria-hidden />
            {lock.unchecked ? 'Its password goes with it — ProDoc checks it as it stores the file.' : 'Opens — its password goes with it, to be kept sealed.'}
          </p>
        )}
        {doc && item.parse && again?.state !== 'uploading' && <ParseLine parse={item.parse} labelOf={labelOf} />}
        {again?.state === 'refused' && (
          <p className={cn('mt-1 text-xs', TONE_TEXT.error)} role="alert">
            {again.fileName} was not stored as a new version: {again.message}
          </p>
        )}
        {doc && reading?.state === 'failed' && reading.reason && PASSWORD_REASONS.includes(reading.reason) && again?.state !== 'uploading' && (
          <UnlockRow item={item} wrong={reading.reason === 'password_incorrect'} />
        )}
        {item.state === 'uploading' && (
          <span
            role="progressbar"
            aria-label={`Uploading ${item.file.name}`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={item.progress}
            className="bg-muted mt-1.5 block h-1 max-w-sm overflow-hidden rounded-full"
          >
            <span className="bg-primary block h-full rounded-full transition-all" style={{ width: `${item.progress}%` }} />
          </span>
        )}
      </div>
      <span className={cn('flex shrink-0 items-center gap-1.5 text-xs font-medium', tone)} data-word={reading?.state}>
        <Icon size={16} className={cn('shrink-0', (item.state === 'uploading' || again?.state === 'uploading' || reading?.state === 'reading') && 'animate-spin')} aria-hidden />
        {label}
        {reading?.reason && reading.state !== 'accepted' && again?.state !== 'uploading' && <span className="text-muted-foreground font-normal">· {readingReasonText(reading.reason)}</span>}
        {doc?.encrypted && (
          <Badge tone="warning" size="sm">
            Encrypted
          </Badge>
        )}
      </span>
      <div className="flex shrink-0 justify-end gap-1 sm:w-32">
        {doc && reading?.state === 'failed' && again?.state !== 'uploading' && <AgainPicker accept={accept} onPick={(file) => void uploadAgain(item.key, file)} />}
        {item.state === 'asking' && (
          <Button variant="outline" size="sm" onClick={onChoose}>
            Choose…
          </Button>
        )}
        {item.state === 'error' && (
          <Button variant="outline" size="sm" onClick={() => retryItem(item.key)}>
            <RotateCcw size={14} aria-hidden />
            Try again
          </Button>
        )}
        {(refused || item.state === 'discarded') && (
          <Button variant="ghost" size="sm" onClick={() => removeItem(item.key)} aria-label={`Remove ${item.file.name} from the list`}>
            <X size={14} aria-hidden />
            Remove
          </Button>
        )}
      </div>
    </li>
  )
}

/**
 * The row's password box (FR-55, D53): *Unlock* asks the same parse again with
 * it — used once and kept nowhere by the service. The box is empty again as soon
 * as it is sent; a wrong one leaves the row *Failed* with *Wrong password*.
 */
function UnlockRow({ item, wrong }: { item: UploadItem; wrong: boolean }) {
  const [value, setValue] = useState('')
  const id = `prodoc-unlock-${item.key}`
  const send = () => {
    if (!value) return
    const password = value
    setValue('')
    void unlockItem(item.key, password)
  }
  return (
    <span className="mt-1.5 flex flex-wrap items-center gap-2" data-unlock={item.key}>
      <label htmlFor={id} className={cn('flex items-center gap-1 text-xs font-medium', wrong ? TONE_TEXT.error : 'text-foreground')}>
        <LockKeyhole size={13} aria-hidden />
        {wrong ? 'Wrong password — try again' : 'Its password'}
      </label>
      <Input
        id={id}
        size="sm"
        type="password"
        autoComplete="off"
        className="w-40"
        value={value}
        aria-invalid={wrong ? true : undefined}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            send()
          }
        }}
      />
      <Button size="sm" variant="outline" disabled={!value} onClick={send}>
        Unlock
      </Button>
    </span>
  )
}

/**
 * W30 (FR-67, D103): the box on a protected PDF's row before it is sent —
 * *Check* tries the password with pdf.js in this tab, *Skip* stores the file
 * locked. Emptied after every try; the password is never shown back.
 */
function PasswordAtUpload({ item }: { item: UploadItem }) {
  const [value, setValue] = useState('')
  const id = `prodoc-upload-password-${item.key}`
  const checking = item.lock?.state === 'checking'
  const wrong = item.lock?.state === 'asking' && item.lock.wrong === true
  const check = () => {
    if (!value || checking) return
    const password = value
    setValue('')
    void checkPassword(item.key, password)
  }
  return (
    <div className="mt-1.5 space-y-1" data-upload-password={item.key}>
      <span className="flex flex-wrap items-center gap-2">
        <label htmlFor={id} className="text-foreground flex items-center gap-1 text-xs font-medium">
          <LockKeyhole size={13} aria-hidden />
          Its password
        </label>
        <Input
          id={id}
          size="sm"
          type="password"
          autoComplete="off"
          className="w-40"
          value={value}
          disabled={checking}
          aria-invalid={wrong ? true : undefined}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              check()
            }
          }}
        />
        <Button size="sm" variant="outline" disabled={!value || checking} onClick={check}>
          {checking ? <LoaderCircle size={14} className="animate-spin" aria-hidden /> : null}
          Check
        </Button>
        <Button size="sm" variant="ghost" disabled={checking} onClick={() => skipPassword(item.key)}>
          Skip — store it locked
        </Button>
      </span>
      {wrong ? (
        <p className={cn('text-xs', TONE_TEXT.error)} role="alert">
          That password doesn’t open this file.
        </p>
      ) : (
        <p className="text-muted-foreground text-xs">Saved with the file, sealed — it opens for whoever may see it, and nobody is shown it. The file stays protected.</p>
      )}
    </div>
  )
}

/** W30 (FR-67): what the service did with the password sent with a stored file — kept, or not and why. Nothing when none was sent. */
function PasswordOutcome({ item }: { item: UploadItem }) {
  if (item.passwordSaved === undefined) return null
  if (item.passwordSaved) {
    return (
      <p className={cn('mt-1 flex items-center gap-1 text-xs', TONE_TEXT.success)} data-password-outcome="saved">
        <CircleCheck size={13} aria-hidden />
        Password saved — it opens for you here; the file stays protected.
      </p>
    )
  }
  return (
    <p className={cn('mt-1 text-xs', TONE_TEXT.warning)} data-password-outcome="not_saved" role="status">
      Password not saved{item.passwordError ? ` — ${passwordErrorText(item.passwordError)}` : '.'} The file is stored, locked; its password can be saved from the list.
    </p>
  )
}

/** *Upload again* (FR-55): one file, stored as the next version of the same document and read as the first was asked. */
function AgainPicker({ accept, onPick }: { accept: string; onPick: (file: File) => void }) {
  const [key, setKey] = useState(0)
  return (
    <FileInput
      key={key}
      accept={accept}
      buttonLabel="Upload again"
      aria-label="Choose a file to upload again as a new version"
      className="w-auto [&_label+span]:sr-only"
      onChange={(e) => {
        const file = e.target.files?.[0]
        if (file) onPick(file)
        setKey((k) => k + 1)
      }}
    />
  )
}

/* ------------------------------------------------------------- duplicate */

const CHOICES: { value: OnDuplicate | 'discard'; label: string; hint: string }[] = [
  { value: 'keep_both', label: 'Keep both', hint: 'Store this as a second, separate document. Both stay.' },
  {
    value: 'new_version',
    label: 'Link as a new version',
    hint: 'The earlier document gets these bytes as its newest version; its older versions are kept.',
  },
  { value: 'discard', label: 'Discard', hint: 'Store nothing. The earlier document stays exactly as it is.' },
]

/**
 * The duplicate choice (FR-3): the same bytes are already a live document
 * this person can see, so nothing is sent until they say what happens. The
 * choice is recorded in the document's audit trail by the service.
 */
function DuplicateDialog({
  item,
  others,
  client,
  nameOf,
  onClose,
  onResolve,
}: {
  item: UploadItem | null
  others: number
  client: ProDocClient
  nameOf: (id: string | null | undefined) => string | null
  onClose: () => void
  onResolve: (item: UploadItem, choice: OnDuplicate | 'discard', linkTo: string | undefined, rest: boolean) => void
}) {
  const [choice, setChoice] = useState<OnDuplicate | 'discard'>('keep_both')
  const [linkTo, setLinkTo] = useState('')
  const [rest, setRest] = useState(false)
  const key = item?.key ?? null
  // A refusal names its twins by id only; the dialog reads what it can show.
  const twins = useLoad<Twin[]>(
    () => (item ? Promise.all(item.twins.map((t) => ('title' in t ? Promise.resolve(t) : client.documents.get(t.id).catch(() => t)))) : Promise.resolve([])),
    [key, client],
  )
  useEffect(() => {
    setChoice('keep_both')
    setLinkTo(item?.twins[0]?.id ?? '')
    setRest(false)
    // A new file in the dialog starts from the safe answer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  if (!item) return null
  const shown = twins.data ?? item.twins
  const full = (t: Twin): t is ProDocDocument => 'title' in t

  return (
    <Dialog open onClose={onClose} title={`“${item.file.name}” is already here`} width="lg">
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault()
          onResolve(item, choice, choice === 'new_version' ? linkTo || shown[0]?.id : undefined, rest)
        }}
      >
        <p className="text-muted-foreground text-sm">
          The same bytes are already stored as {shown.length === 1 ? 'this document' : `these ${shown.length} documents`}. Nothing has been sent; ProDoc never
          merges a copy on its own.
        </p>
        <ul className="divide-border border-border divide-y rounded-lg border">
          {twins.loading && item.twins.some((t) => !full(t)) ? (
            <li className="p-3">
              <Skeleton className="h-5 w-2/3" />
            </li>
          ) : (
            shown.map((t) => (
              <li key={t.id} className="flex items-center gap-3 p-3">
                {full(t) ? (
                  <>
                    <FileMark mediaType={t.media_type} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="text-foreground truncate text-sm font-medium">{t.title}</p>
                      <p className="text-muted-foreground text-xs">
                        {[nameOf(t.workspace_id), `uploaded ${when(t.created_at)}`, t.version_count > 1 ? `${t.version_count} versions` : null].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <span className="text-muted-foreground shrink-0 text-xs">{formatDateTime(t.created_at)}</span>
                  </>
                ) : (
                  <p className="text-muted-foreground text-sm">An earlier document in this organisation.</p>
                )}
              </li>
            ))
          )}
        </ul>
        <RadioGroup
          legend="What should happen to this file?"
          options={CHOICES.map((c) => ({ value: c.value, label: c.label, description: c.hint }))}
          value={choice}
          onChange={setChoice}
        />
        {choice === 'new_version' && shown.length > 1 && (
          <Select label="The new version of" value={linkTo} onChange={(e) => setLinkTo(e.target.value)}>
            {shown.map((t) => (
              <option key={t.id} value={t.id}>
                {full(t) ? t.title : t.id}
              </option>
            ))}
          </Select>
        )}
        {others > 0 && (
          <Checkbox
            label={`Do the same for the other ${plural(others, 'file')} already here`}
            checked={rest}
            onChange={(e) => setRest(e.target.checked)}
          />
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Decide later
          </Button>
          <Button type="submit" variant={choice === 'discard' ? 'destructive' : 'primary'}>
            {choice === 'keep_both' ? 'Keep both' : choice === 'new_version' ? 'Link as a new version' : 'Discard this file'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

/* ------------------------------------------------------------------ drop */

/** The kit's file control. It names one chosen file, and a batch is many —
 *  so its "No file chosen" line is kept for screen readers only, and it is
 *  remounted after each pick. */
function Picker({ accept, onAdd, label }: { accept: string; onAdd: (files: FileList | null) => void; label: string }) {
  const [key, setKey] = useState(0)
  return (
    <FileInput
      key={key}
      multiple
      accept={accept}
      buttonLabel={label}
      aria-label="Choose files to upload"
      className="w-auto [&_label+span]:sr-only"
      onChange={(e) => {
        onAdd(e.target.files)
        setKey((k) => k + 1)
      }}
    />
  )
}

/** The drop's entries, taken **in the drop event** — the items are gone once it returns. Directories among them make it a folder drop. */
export function entriesOf(data: DataTransfer | null | undefined): FileSystemEntry[] {
  const entries: FileSystemEntry[] = []
  for (const item of Array.from(data?.items ?? [])) {
    if (item.kind !== 'file' || typeof item.webkitGetAsEntry !== 'function') continue
    const entry = item.webkitGetAsEntry()
    if (entry) entries.push(entry)
  }
  return entries
}

/** True while files are being dragged over the window; calls `onDrop` with
 *  them, or `onFolder` with the entries when a folder is among them (FR-7).
 *  A counter, because `dragenter`/`dragleave` fire for every child. */
function useWindowDrop(onDrop: (files: FileList) => void, onFolder?: (entries: FileSystemEntry[]) => void): boolean {
  const [dragging, setDragging] = useState(false)
  const depth = useRef(0)
  const handler = useRef(onDrop)
  handler.current = onDrop
  const folderHandler = useRef(onFolder)
  folderHandler.current = onFolder

  useEffect(() => {
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files')
    const enter = (e: DragEvent) => {
      if (!hasFiles(e)) return
      depth.current += 1
      setDragging(true)
    }
    const leave = (e: DragEvent) => {
      if (!hasFiles(e)) return
      depth.current = Math.max(0, depth.current - 1)
      if (depth.current === 0) setDragging(false)
    }
    const over = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault()
    }
    const drop = (e: DragEvent) => {
      if (!hasFiles(e)) return
      e.preventDefault()
      depth.current = 0
      setDragging(false)
      const entries = entriesOf(e.dataTransfer)
      if (folderHandler.current && entries.some((entry) => entry.isDirectory)) folderHandler.current(entries)
      else if (e.dataTransfer?.files.length) handler.current(e.dataTransfer.files)
    }
    window.addEventListener('dragenter', enter)
    window.addEventListener('dragleave', leave)
    window.addEventListener('dragover', over)
    window.addEventListener('drop', drop)
    return () => {
      window.removeEventListener('dragenter', enter)
      window.removeEventListener('dragleave', leave)
      window.removeEventListener('dragover', over)
      window.removeEventListener('drop', drop)
    }
  }, [])

  return dragging
}

/** "PDF, images, spreadsheets, CSV, Word documents, JSON, text files, e-mails and ZIP archives" — from the served list, never a constant. */
function describeTypes(mediaTypes: string[]): string {
  const words = new Set<string>()
  for (const t of mediaTypes) {
    if (t === 'application/pdf') words.add('PDF')
    else if (t.startsWith('image/')) words.add('images')
    else if (t === 'text/csv') words.add('CSV')
    else if (t === 'application/json') words.add('JSON')
    else if (t === 'text/plain') words.add('text files')
    else if (t === 'message/rfc822' || t === 'application/vnd.ms-outlook') words.add('e-mails')
    else if (t === 'application/zip') words.add('ZIP archives')
    else if (t.includes('spreadsheet') || t.includes('ms-excel')) words.add('spreadsheets')
    else if (t.includes('wordprocessingml')) words.add('Word documents')
    else words.add(t)
  }
  const list = [...words]
  if (list.length <= 1) return list[0] ?? 'Files'
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`
}
